import { getStoredLoadout, getWieldedLoadout } from './equipped-items';
import { isEquippedItem, normalizeSkill, resolveEffectiveCoreStats } from './skills';
import type { Character, CharacterEquipmentLoadout, Equipment, Skill } from './types/game';
import { hasSkillPerk, isDualWielding, QUICK_HANDS_COOLDOWN_FACTOR } from './skill-perks';
import { resolveMainHandWeapon, resolveOffHandWeapon } from './wielded-weapons';
import { resolveAttackStyle, resolveFocusSkillLevel } from './attack-math';

export const DEFAULT_PRIMARY_ATTACK_COOLDOWN_MS = 940;
export const DEFAULT_OFF_HAND_ATTACK_COOLDOWN_MS = 1150;
export const MIN_PRIMARY_ATTACK_COOLDOWN_MS = 300;
export const MIN_OFF_HAND_ATTACK_COOLDOWN_MS = 380;
/**
 * Cooldown reduction, in ms, granted per point of effective attack speed
 * (core stat + skill level + equipment bonus).
 *
 * Reduced alongside the lower base cadences above: at the old 55ms/point a
 * baseline character's ~8 speed removed 440ms, which is most of a sub-second
 * base cooldown and left almost no room for the stat to scale into.
 */
export const ATTACK_SPEED_MS_PER_POINT = 30;

/**
 * Fraction of a swing's cadence the attacker is *committed* to it - facing
 * locked, movement stopped, no other action admitted. The rest of the
 * cadence is recovery: the attack is still on cooldown, but the character is
 * free to move or dodge out of it.
 *
 * This split is the difference between an attack that feels responsive and
 * one that feels gated. Committing for the whole cooldown (which is what
 * this system did before the windows were separated) turns every swing into
 * a full stop, because `AttackCooldownTracker` *already* gates the next
 * attack independently - the movement lock bought nothing.
 */
export const ATTACK_COMMIT_FRACTION = 0.3;
/** Floor on the commitment window, so even the fastest weapon reads as a deliberate swing. */
export const MIN_ATTACK_COMMIT_MS = 120;
/** Ceiling on the commitment window, so a slow weapon doesn't become a full stop again. */
export const MAX_ATTACK_COMMIT_MS = 320;
/**
 * How early an attack input is accepted before its cooldown has elapsed. The
 * queued press fires the moment the cooldown clears rather than being
 * discarded, which is what makes repeated attacking feel connected instead
 * of eating inputs. Kept short on purpose: long buffers make combat feel
 * spammy and out of the player's control.
 */
export const ATTACK_INPUT_BUFFER_MS = 180;

/**
 * How long an NPC visibly winds up before its attack lands.
 *
 * NPC attacks used to resolve on the tick after they were decided, with no
 * windup at all - damage simply appeared, which makes a hit impossible to
 * read, anticipate, or dodge. A telegraph is what converts enemy damage from
 * something that happens *to* the player into something they had a chance to
 * answer, and it is the precondition for a dodge being worth having.
 *
 * Scaled by the attack's own cadence so heavier, slower attacks telegraph
 * more obviously than quick ones, then clamped so nothing is unreadably fast
 * or comically slow.
 */
export const NPC_ATTACK_WINDUP_FRACTION = 0.45;
export const MIN_NPC_ATTACK_WINDUP_MS = 350;
export const MAX_NPC_ATTACK_WINDUP_MS = 700;

/** Resolves the telegraph duration for one NPC attack from its cadence. */
export function resolveNpcAttackWindupMs(cooldownMs: number): number {
    return Math.max(
        MIN_NPC_ATTACK_WINDUP_MS,
        Math.min(MAX_NPC_ATTACK_WINDUP_MS, cooldownMs * NPC_ATTACK_WINDUP_FRACTION),
    );
}

export interface AttackTiming {
    /** Weapon-defined cadence before character speed modifiers. */
    baseCooldownMs: number;
    /** Effective cadence after character, skill, and equipment speed modifiers. */
    cooldownMs: number;
    /**
     * How long the attacker is committed to the swing: movement stopped,
     * facing locked, no other action admitted. Always <= `cooldownMs`.
     */
    commitMs: number;
    /**
     * The remainder of the cadence after the commitment ends. The attack is
     * still cooling down, but the character can move or dodge out of it - so
     * this is the window that keeps combat from feeling like a series of
     * full stops.
     */
    recoveryMs: number;
    /**
     * First-guess animation playback multiplier, derived from cadence alone.
     * The client replaces this with `resolveAttackPlaybackSpeed()` as soon as
     * the render worker reports the clip's real length - only the worker knows
     * that, so this value cannot size the swing correctly on its own.
     */
    playbackSpeed: number;
}

/**
 * Splits one cadence into its committed and recoverable halves. Exported so
 * the client can predict the same movement lock the host will apply without
 * duplicating the arithmetic.
 */
export function resolveAttackCommitMs(cooldownMs: number): number {
    return Math.min(
        cooldownMs,
        Math.max(MIN_ATTACK_COMMIT_MS, Math.min(MAX_ATTACK_COMMIT_MS, cooldownMs * ATTACK_COMMIT_FRACTION)),
    );
}

/**
 * How long the swing *animation* should take, relative to the committed
 * window. Well above 1.0, so the follow-through trails past the point where
 * control returns — that trailing part is what reads as weight, and it costs
 * nothing in responsiveness because the movement lock ends with the commit
 * window, not with the animation.
 *
 * History, because this has now been wrong in both directions. Authored swing
 * clips run well over a second. Originally they played at roughly 1.35x, a
 * ~740ms visible swing, which read as *sluggish* — the character was still
 * mid-swing when the next attack was already off cooldown. The correction
 * retargeted the swing to 1.25x the commit window, i.e. ~285ms for a 760ms
 * weapon at ~3.5x playback, and that overshot: a one-second animation crammed
 * into 285ms reads as twitchy and disconnected from the character rather than
 * fast. This value sits between the two, nearer the responsive end.
 *
 * Tune it with the Character Debugger's gameplay-action harness
 * (`/debug/characters`), which sweeps this factor live and shows the resulting
 * swing duration and playback ratio.
 */
export const ATTACK_SWING_VISUAL_FACTOR = 2.25;

/**
 * Ceiling on how much an authored attack clip may be sped up to hit its
 * target swing duration. Authored swing clips are long relative to an action
 * game's cadence, so without a real speed-up the character is still mid-swing
 * when the next attack is already allowed - which is what makes attacking
 * feel sluggish no matter how short the cooldown is.
 */
export const MAX_ATTACK_PLAYBACK_SPEED = 5;

/**
 * The wall-clock duration one swing animation should occupy, derived from the
 * committed window rather than from the weapon's whole cadence. Sizing the
 * visual to the cadence (the previous model) meant the swing filled the entire
 * gap between attacks, leaving no readable "ready again" beat.
 */
export function resolveAttackSwingMs(
    commitMs: number,
    visualFactor: number = ATTACK_SWING_VISUAL_FACTOR,
): number {
    return Math.max(1, commitMs * visualFactor);
}

/**
 * Playback speed needed to fit an authored clip of `clipDurationMs` into this
 * swing's target duration.
 *
 * Only the client can call this usefully: the real clip length lives in the
 * render worker's `AnimationGroup` data, which is why `resolveAttackTiming`'s
 * own `playbackSpeed` can only ever be a cadence-derived first guess. Never
 * slows a clip below its authored rate - a clip already shorter than the
 * target simply plays at 1x.
 */
export function resolveAttackPlaybackSpeed(
    clipDurationMs: number,
    commitMs: number,
    visualFactor: number = ATTACK_SWING_VISUAL_FACTOR,
): number {
    if (!Number.isFinite(clipDurationMs) || clipDurationMs <= 0) {
        return 1;
    }

    const targetMs = resolveAttackSwingMs(commitMs, visualFactor);
    return Math.max(1, Math.min(MAX_ATTACK_PLAYBACK_SPEED, clipDurationMs / targetMs));
}

/** The character's gear as wielded; undefined for one with no loadout at all. */
function resolveWieldedGear(character: Character): CharacterEquipmentLoadout | undefined {
    return getStoredLoadout(character) ? getWieldedLoadout(character) : undefined;
}

function resolveWeapon(equipment: CharacterEquipmentLoadout | undefined, isOffHand: boolean): Equipment | undefined {
    return isOffHand ? resolveOffHandWeapon(equipment) : resolveMainHandWeapon(equipment);
}

/**
 * A swing's cooldown from its base cadence: each point of attack speed takes
 * `ATTACK_SPEED_MS_PER_POINT` off it, `cadenceFactor` (Quick Hands)
 * scales it, and it never drops below the hand's minimum. The one cadence
 * rule for combat, the power score and enemy damage ratings.
 */
export function resolveCadenceCooldownMs(baseCooldownMs: number, attackSpeed: number, isOffHand: boolean, cadenceFactor = 1): number {
    const minimumCooldownMs = isOffHand ? MIN_OFF_HAND_ATTACK_COOLDOWN_MS : MIN_PRIMARY_ATTACK_COOLDOWN_MS;
    return Math.max(minimumCooldownMs, (baseCooldownMs - attackSpeed * ATTACK_SPEED_MS_PER_POINT) * cadenceFactor);
}

/** What quickens a character's cadence beyond attack speed: Quick Hands, for a dual wielder. */
export function resolveCadenceFactor(skills: readonly Skill[], equipment: CharacterEquipmentLoadout | undefined): number {
    return isDualWielding(equipment) && hasSkillPerk(skills, 'quick-hands') ? QUICK_HANDS_COOLDOWN_FACTOR : 1;
}

function resolveEquipmentSpeedBonus(equipment: CharacterEquipmentLoadout | undefined): number {
    if (!equipment) {
        return 0;
    }

    return [
        ...equipment.weapons,
        equipment.armor,
        ...equipment.miscellaneous,
    ]
        .filter(isEquippedItem)
        .reduce((total, item) => total + (item.combat?.speedBonus ?? 0), 0);
}

/** Returns the configured base cadence for one weapon slot. */
export function resolveWeaponBaseAttackCooldownMs(
    weapon: Equipment | undefined,
    isOffHand: boolean = false,
): number {
    const fallback = isOffHand
        ? DEFAULT_OFF_HAND_ATTACK_COOLDOWN_MS
        : DEFAULT_PRIMARY_ATTACK_COOLDOWN_MS;
    const configured = weapon?.combat?.attackCooldownMs;
    return typeof configured === 'number' && Number.isFinite(configured) && configured > 0
        ? configured
        : fallback;
}

/** Resolves weapon cadence and animation playback speed for one attack action. */
export function resolveAttackTiming(character: Character, isOffHand: boolean = false): AttackTiming {
    const equipment = resolveWieldedGear(character);
    const weapon = resolveWeapon(equipment, isOffHand);
    // A character with an authored natural cadence (an NPC) uses it in place
    // of the shared default, so enemy attack rate is a per-creature design
    // value rather than a side effect of tuning the player's attack feel. It
    // wins over a held weapon too: arming an NPC copies the weapon's cadence
    // into it (withNpcWeapons), so haste and enrage, which scale it, apply.
    const naturalCooldownMs = character.naturalAttackCooldownMs;
    const baseCooldownMs = typeof naturalCooldownMs === 'number'
        && Number.isFinite(naturalCooldownMs)
        && naturalCooldownMs > 0
        ? naturalCooldownMs
        : resolveWeaponBaseAttackCooldownMs(weapon, isOffHand);
    const skills = (character.skills ?? []).map(normalizeSkill);
    const style = resolveAttackStyle(weapon);
    const skillLevel = resolveFocusSkillLevel(skills, style, equipment);
    const coreStats = resolveEffectiveCoreStats(character, skills);
    const attackSpeed = (coreStats?.speed ?? 0) + skillLevel + resolveEquipmentSpeedBonus(equipment);
    const cooldownMs = resolveCadenceCooldownMs(baseCooldownMs, attackSpeed, isOffHand, resolveCadenceFactor(skills, equipment));

    const commitMs = resolveAttackCommitMs(cooldownMs);

    return {
        baseCooldownMs,
        cooldownMs,
        commitMs,
        recoveryMs: Math.max(0, cooldownMs - commitMs),
        playbackSpeed: baseCooldownMs / cooldownMs,
    };
}
